import React, { useEffect, useMemo, useState } from "react";
import { Chip, Box, Avatar, Typography, Checkbox, Button, useMediaQuery, Card, IconButton, Tooltip } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import CloseIcon from "@mui/icons-material/Close";
import SaveBorderIcon from "../../../../app/assets/images/icons/Bookmark.svg";
import SaveFilledIcon from "../../../../app/assets/images/icons/FilledBookmark.svg";
import EngineeringDefaultProfile from "../../../../app/assets/images/pictures/engineering_default_profile.png";
import ArchitectureDefaultProfile from "../../../../app/assets/images/pictures/architecture_default_profile.png";
import ConstructionDefaultProfile from "../../../../app/assets/images/pictures/construction_default_profile.png";
import { alpha, useTheme } from "@mui/material/styles";
import { humanizeRegion } from "bundles/common/utils/utils";
import sh from "bundles/common/utils/sh";
import CloseJobApplicationModal from "./CloseJobApplicationModal";

const DENIAL_REASON_OPTIONS = ["wrong_diploma", "wrong_xp", "insufficient_languages", "closed_position", "other"];

const getAvailabilityChipConfig = (availability, theme) => {
  const configs = {
    looking: {
      labelId: "recruiter.actively_looking",
      backgroundColor: theme.palette.secondary.light,
      color: theme.palette.primary.main,
      borderColor: alpha(theme.palette.primary.main, 0.14),
    },
    listening: {
      labelId: "recruiter.open_to_opportunities",
      backgroundColor: alpha("#FF9800", 0.1),
      color: "#F57C00",
      borderColor: alpha("#F57C00", 0.14),
    },
    currently_employed: {
      labelId: "recruiter.currently_employed",
      backgroundColor: alpha("#9E9E9E", 0.1),
      color: "#616161",
      borderColor: alpha("#616161", 0.14),
    },
  };
  return configs[availability];
};

const getSectorDefaultAvatar = (desiredSector) => {
  if (!desiredSector) return null;

  const sectorMap = {
    engineering: EngineeringDefaultProfile,
    architecture: ArchitectureDefaultProfile,
    construction: ConstructionDefaultProfile,
  };

  return sectorMap[desiredSector] || null;
};

const CandidateCardNew = ({
  candidate,
  jobApplications = [],
  handleFavoriteToggle = () => {},
  favorites = [],
  showCloseIcon = true,
  chipVariant = null, // "availability" | "team"
  onActionButtonClick = null,
  secondaryActionButtonLabel = null,
  onSecondaryActionButtonClick = null,
  secondaryActionDisabled = false,
  pinSaveIconToBottom = false,
  showSaveIcon = true,
  showMetaChips = true,
  showLastActive = false,
  onCardClick = null,
  onJobApplicationClosed = null,
  onKanbanMetaUpdate = null,
  columnKey = null,
}) => {
  const [showAllJobApplications, setShowAllJobApplications] = useState(false);
  const [closingAppId, setClosingAppId] = useState(null);
  const [localJobApplications, setLocalJobApplications] = useState([]);
  const [isCloseApplicationModalOpen, setIsCloseApplicationModalOpen] = useState(false);
  const [pendingCloseApplication, setPendingCloseApplication] = useState(null);
  const [selectedDenialReasons, setSelectedDenialReasons] = useState([]);
  const [otherDenialReason, setOtherDenialReason] = useState("");

  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const authenticityToken = useSelector((state) => state.authenticity_token);

  const handleShowMoreClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setShowAllJobApplications(true);
  };

  const appsFromProps = useMemo(() => {
    return Array.isArray(jobApplications) ? jobApplications : [];
  }, [jobApplications]);

  const appsKey = useMemo(() => {
    return (appsFromProps || []).map((app) => `${app?.id}:${app?.status}`).join(",");
  }, [appsFromProps]);

  useEffect(() => {
    setLocalJobApplications((prev) => {
      const prevKey = (prev || []).map((app) => `${app?.id}:${app?.status}`).join(",");
      if (prevKey === appsKey) return prev;
      return appsFromProps;
    });
  }, [appsFromProps, appsKey]);

  const hasApps = Array.isArray(localJobApplications) && localJobApplications.length > 0;

  const firstJobApplication = hasApps
    ? localJobApplications.find((app) => app?.status !== "closed") || localJobApplications[0]
    : null;

  const primaryJobLabel =
    firstJobApplication?.job_offer?.job_title ||
    (firstJobApplication ? intl.formatMessage({ id: "recruiter.kanban.spontaneous_application" }) : null);

  const additionalApplicationsCount = localJobApplications?.length > 1 ? localJobApplications.length - 1 : 0;

  const openCloseApplicationModal = (appToClose) => {
    if (!appToClose?.id || appToClose?.status === "closed" || closingAppId) return;
    setPendingCloseApplication(appToClose);
    setSelectedDenialReasons(
      Array.isArray(appToClose?.denial_reasons)
        ? appToClose.denial_reasons.filter((reason) => DENIAL_REASON_OPTIONS.includes(reason))
        : []
    );
    setOtherDenialReason("");
    setIsCloseApplicationModalOpen(true);
  };

  const closeCloseApplicationModal = () => {
    if (closingAppId) return;
    setIsCloseApplicationModalOpen(false);
    setPendingCloseApplication(null);
    setSelectedDenialReasons([]);
    setOtherDenialReason("");
  };

  const toggleDenialReason = (reasonKey) => {
    setSelectedDenialReasons((prev) => {
      const isSelected = prev.includes(reasonKey);
      if (isSelected) {
        if (reasonKey === "other") setOtherDenialReason("");
        return prev.filter((item) => item !== reasonKey);
      }
      return [...prev, reasonKey];
    });
  };

  const handleCloseJobApplication = async (appToClose) => {
    if (!appToClose?.id || appToClose?.status === "closed" || closingAppId) return;
    
    const denialReasons = selectedDenialReasons
      .filter((reason) => reason !== "other")
      .concat(
        selectedDenialReasons.includes("other") && otherDenialReason.trim()
          ? [otherDenialReason.trim()]
          : selectedDenialReasons.includes("other")
            ? ["other"]
            : []
      );

    setClosingAppId(appToClose.id);
    const updatedApps = localJobApplications.map((app) => (app.id === appToClose.id ? { ...app, status: "closed", denial_reasons: denialReasons } : app));
    const remainingActive = updatedApps.filter((app) => app.status !== "closed");

    setLocalJobApplications(updatedApps);

    try {
      let res = null;
      if (appToClose?.is_external) {
        res = await sh.put(`update_external_candidate_application_status/${appToClose.id}`, {
          authenticity_token: authenticityToken,
          external_candidate_id: appToClose?.external_candidate_id,
          status: "closed",
          denial_reasons: denialReasons,
        });
      } else {
        res = await sh.put(`update_job_application_status/${appToClose.id}`, {
          authenticity_token: authenticityToken,
          candidate_id: candidate?.id || appToClose?.candidate_id,
          status: "closed",
          denial_reasons: denialReasons,
        });
      }

      const kanbanMeta = res?.data?.kanban;
      if (kanbanMeta && typeof onKanbanMetaUpdate === "function") {
        onKanbanMetaUpdate(kanbanMeta);
      }

      if (typeof onJobApplicationClosed === "function") {
        onJobApplicationClosed({
          ...appToClose,
          status: "closed",
          all_closed: remainingActive.length === 0,
          updated_job_applications: updatedApps,
          kanban_card: res?.data?.kanban_card,
        });
      }

      enqueueSnackbar(intl.formatMessage({ id: "applicant.closed_success" }), { variant: "success" });
    } catch (err) {
      console.error(err);
      setLocalJobApplications((prev) =>
        prev.map((app) => (app.id === appToClose.id ? { ...app, status: appToClose.status } : app))
      );
      enqueueSnackbar(intl.formatMessage({ id: "recruiter.kanban.close_failed" }), { variant: "error" });
    } finally {
      setClosingAppId(null);
      setIsCloseApplicationModalOpen(false);
      setPendingCloseApplication(null);
      setSelectedDenialReasons([]);
      setOtherDenialReason("");
    }
  };

  const jobApplicationItems = (() => {
    if (hasApps) {
      return localJobApplications
        .map((app) => ({
          id: app?.id,
          created_at: app?.created_at,
          candidate_id: app?.candidate_id,
          external_candidate_id: app?.external_candidate_id,
          is_external: Boolean(app?.is_external),
          status: app?.status,
          denial_reasons: app?.denial_reasons,
          title: app?.job_offer?.job_title || intl.formatMessage({ id: "recruiter.kanban.spontaneous_application" }),
          isClosed: app?.status === "closed",
        }))
        .filter((app) => Boolean(app.title));
    }

    return primaryJobLabel ? [{ id: "fallback-primary", title: primaryJobLabel, isClosed: false }] : [];
  })();

  const formatJobApplicationCreatedAt = (createdAt) => {
    if (!createdAt) return "";
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const theme = useTheme();
  const experiencePastilleVisible = (() => {
    if (!showMetaChips || candidate?.experience_year == null) return false;
    const raw = String(candidate.experience_year).trim();
    if (!raw) return false;
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n === 0) return false;
    return true;
  })();
  const availabilityChipVisible = showMetaChips && chipVariant === "availability" && candidate?.availability;
  // "Last active" is only shown when the candidate was active within the last 30 days;
  // never render an "inactive" pill.
  const lastActiveDays = (() => {
    const raw = candidate?.new_last_sign_in_at;
    if (!raw) return null;
    const ts = new Date(raw).getTime();
    if (Number.isNaN(ts)) return null;
    return Math.floor((Date.now() - ts) / (1000 * 60 * 60 * 24));
  })();
  const lastActivePillVisible = showLastActive && lastActiveDays != null && lastActiveDays <= 30;
  const lastActiveChipConfig =
    lastActiveDays != null && lastActiveDays < 7
      ? {
        backgroundColor: theme.palette.secondary.light,
        color: theme.palette.primary.main,
        borderColor: alpha(theme.palette.primary.main, 0.14),
      }
      : {
        backgroundColor: alpha("#FF9800", 0.1),
        color: "#F57C00",
        borderColor: alpha("#F57C00", 0.14),
      };
  const isInformationPresent =
    Boolean(candidate?.candidate_status) ||
    experiencePastilleVisible ||
    Boolean(candidate?.region || candidate?.city) ||
    availabilityChipVisible;
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const useStyles = makeStyles(() => ({
    cardContainer: {
      padding: "20px",
      borderRadius: "12px",
      boxShadow: "none",
      position: "relative",
      overflow: isDesktop ? "visible" : "hidden",
    },
    candidateStatusButton: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      borderRadius: "999px",
      color: `${theme.palette.secondary.black} !important`,
      textTransform: "none",
      padding: "4px 12px",
      height: "30px",
      fontSize: "12px",
    },
  }));

  const classes = useStyles();
  const isClickable = typeof onCardClick === "function";

  return (
    <>
      <Card
        className={classes.cardContainer}
        onClick={(e) => {
          if (!isClickable) return;
          e.preventDefault();
          onCardClick(candidate);
        }}
        role={isClickable ? "button" : undefined}
        tabIndex={isClickable ? 0 : undefined}
        sx={{
          position: "relative",
          width: "100%",
          cursor: isClickable ? "pointer" : "drag",
        }}
      >
        {candidate?.is_external && isDesktop ? (
          <Chip
            label={<FormattedMessage id="recruiter.kanban.external_badge" defaultMessage="External" />}
            color="primary"
            size="small"
            sx={{ position: "absolute", top: -12, right: 16, zIndex: 1 }}
          />
        ) : chipVariant === "team" && localJobApplications?.some((app) => app?.sent_from_talent_team) && isDesktop ? (
          <Tooltip title={<FormattedMessage id="recruiter.kanban.talent_team_tooltip" />} placement="top" arrow>
            <Chip
              label={<FormattedMessage id="recruiter.kanban.sent_from_talent_team" />}
              color="primary"
              size="small"
              sx={{ position: "absolute", top: -12, right: 16, zIndex: 1 }}
            />
          </Tooltip>
        ) : chipVariant === "team" && localJobApplications?.some((app) => app?.added_from_cvtheque) && isDesktop ? (
          <Tooltip title={<FormattedMessage id="recruiter.kanban.cvtheque_tooltip" defaultMessage="Added from CVthèque" />} placement="top" arrow>
            <Chip
              label={<FormattedMessage id="recruiter.kanban.added_from_cvtheque" defaultMessage="CVthèque" />}
              color="primary"
              size="small"
              sx={{ position: "absolute", top: -12, right: 16, zIndex: 1 }}
            />
          </Tooltip>
        ) : null}

        <Box sx={{display: "flex", gap: 2}}>
          <Avatar
            src={candidate?.profile_picture_url || getSectorDefaultAvatar(candidate?.desired_sector?.[0])}
            alt={`${candidate?.first_name} ${candidate?.last_name}`}
            sx={{ width: isDesktop ? 64 : 48, height: isDesktop ? 64 : 48, backgroundColor: "#9e9e9e", borderRadius: "0" }}
          />
                                    
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
            {/* Name row — actively looking chip inside card, aligned with name */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "600",
                  fontFamily: "Segoe UI Semibold, sans-serif",
                  fontSize: "20px !important",
                  color: theme.palette.text.main,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {[candidate?.first_name, candidate?.last_name].filter(Boolean).join(" ")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
                {lastActivePillVisible && (
                  <Chip
                    label={
                      <FormattedMessage
                        id="recruiter.last_active"
                        defaultMessage="Active {count, plural, =0 {today} one {# day ago} other {# days ago}}"
                        values={{ count: lastActiveDays }}
                      />
                    }
                    size="small"
                    sx={{
                      height: 28,
                      backgroundColor: lastActiveChipConfig.backgroundColor,
                      color: lastActiveChipConfig.color,
                      fontWeight: 600,
                      fontSize: "12px",
                      fontFamily: "Segoe UI Semibold, sans-serif",
                      border: `1px solid ${lastActiveChipConfig.borderColor}`,
                      "& .MuiChip-label": { px: 1.25, py: 0 },
                    }}
                  />
                )}
                {availabilityChipVisible && (() => {
                  const config = getAvailabilityChipConfig(candidate?.availability, theme);
                  if (!config) return null;
                  return (
                    <Chip
                      label={<FormattedMessage id={config.labelId} />}
                      size="small"
                      sx={{
                        height: 28,
                        maxWidth: { xs: "min(100%, 200px)", sm: "none" },
                        backgroundColor: config.backgroundColor,
                        color: config.color,
                        fontWeight: 600,
                        fontSize: "12px",
                        fontFamily: "Segoe UI Semibold, sans-serif",
                        border: `1px solid ${config.borderColor}`,
                        "& .MuiChip-label": { px: 1.25, py: 0, overflow: "hidden", textOverflow: "ellipsis" },
                      }}
                    />
                  );
                })()}
                {showCloseIcon ? (
                  <IconButton
                    size="small"
                    sx={{ p: 0, flexShrink: 0 }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleFavoriteToggle(candidate?.id, "reject");
                    }}
                  >
                    <CloseIcon fontSize="medium" />
                  </IconButton>
                ) : null}
              </Box>
            </Box>
                                        
            {/* Job title row */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center"}}>
              <Typography variant="body1" sx={{ textTransform: candidate?.job_title?.trim() ? "capitalize" : "none", fontSize: "14px", color: theme.palette.text.main }}>
                {candidate?.job_title?.trim() ? candidate.job_title : <FormattedMessage id="recruiter.no_job_title" />}
              </Typography>
              {!pinSaveIconToBottom && !isInformationPresent && showSaveIcon ? (
                <Checkbox
                  icon={<img src={SaveBorderIcon} alt="save" style={{ width: 24, height: 24 }} />}
                  checkedIcon={<img src={SaveFilledIcon} alt="saved" style={{ width: 24, height: 24 }} />}
                  checked={favorites.includes(candidate?.id) || false} 
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFavoriteToggle(candidate?.id, "toggle");
                  }}
                  sx={{ p: 0, display: isDesktop ? "none" : "block" }}
                />
              ) : null}
            </Box>
          </Box>
        </Box>
        {jobApplicationItems.length > 0 ? (
          <Box sx={{ mt: 1, width: "100%" }}>
          
            {showAllJobApplications ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {jobApplicationItems.map((app, idx) => (
                  <Box
                    key={app.id || `${idx}-${app.title}`}
                    sx={{
                      width: "100%",
                      backgroundColor: app.isClosed ? "rgba(0, 0, 0, 0.04)" : theme.palette.secondary.box,
                      opacity: app.isClosed ? 0.5 : 1,
                      transition: "opacity 0.2s ease",
                      borderRadius: "4px",
                      padding: "6px 10px",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                      {(() => {
                        const createdAtLabel = formatJobApplicationCreatedAt(app.created_at);
                        return (
                          <Tooltip title={createdAtLabel} placement="top" arrow>
                            <Typography
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: "12px",
                                fontFamily: "Segoe UI Semibold, sans-serif",
                                color: theme.palette.secondary.black,
                                lineHeight: "16px",
                              }}
                              noWrap
                              title={primaryJobLabel}
                            >
                              {app.title}
                            </Typography>
                          </Tooltip>
                        );
                      })()}
                      {!app.isClosed && columnKey !== "hired" && (
                        <Button
                          size="small"
                          disabled={closingAppId === app.id}
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openCloseApplicationModal(app);
                          }}
                          startIcon={<CloseIcon sx={{ width: 14, height: 14 }} />}
                          sx={{
                            textTransform: "none",
                            fontSize: "11px",
                            fontFamily: "Segoe UI Semibold, sans-serif",
                            borderRadius: "999px",
                            padding: "2px 8px",
                            height: "30px",
                            minWidth: "auto",
                            lineHeight: 1.2,
                            color: theme.palette.text.secondary,
                            border: `1px solid ${theme.palette.divider}`,
                            "&:hover": {
                              color: theme.palette.error.main,
                              borderColor: theme.palette.error.main,
                              backgroundColor: "rgba(211, 47, 47, 0.06)",
                            },
                          }}
                        >
                          {intl.formatMessage({ id: "recruiter.kanban.close" })}
                        </Button>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    opacity: firstJobApplication?.status === "closed" ? 0.5 : 1,
                    backgroundColor: firstJobApplication?.status === "closed" ? "rgba(0, 0, 0, 0.04)" : theme.palette.secondary.box,
                    width: "100%",
                    borderRadius: "4px",
                    padding: "6px 10px",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    {(() => {
                      const createdAtLabel = formatJobApplicationCreatedAt(firstJobApplication?.created_at);
                      return (
                        <Tooltip title={createdAtLabel} placement="top" arrow>
                          <Typography
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              fontSize: "12px",
                              fontFamily: "Segoe UI Semibold, sans-serif",
                              color: theme.palette.secondary.black,
                              lineHeight: "16px",
                            }}
                            noWrap
                            title={primaryJobLabel}
                          >
                            {primaryJobLabel}
                          </Typography>
                        </Tooltip>
                      );
                    })()}
                    {firstJobApplication?.status !== "closed" && columnKey !== "hired" && (
                      <Button
                        size="small"
                        disabled={closingAppId === firstJobApplication?.id}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openCloseApplicationModal(firstJobApplication);
                        }}
                        startIcon={<CloseIcon sx={{ width: 14, height: 14 }} />}
                        sx={{
                          textTransform: "none",
                          fontSize: "11px",
                          fontFamily: "Segoe UI Semibold, sans-serif",
                          borderRadius: "999px",
                          padding: "2px 8px",
                          minWidth: "auto",
                          height: "30px",
                          lineHeight: 1.2,
                          color: theme.palette.text.secondary,
                          border: `1px solid ${theme.palette.divider}`,
                          "&:hover": {
                            color: theme.palette.error.main,
                            borderColor: theme.palette.error.main,
                            backgroundColor: "rgba(211, 47, 47, 0.06)",
                          },
                        }}
                      >
                        {intl.formatMessage({ id: "recruiter.kanban.close" })}
                      </Button>
                    )}
                  </Box>
                </Box>

                {additionalApplicationsCount > 0 && (
                  <>
                    <Button
                      size="small"
                      onClick={handleShowMoreClick}
                      onPointerDown={(e) => e.stopPropagation()}
                      sx={{
                        mt: 0.5,
                        minWidth: "auto",
                        fontSize: "11px",
                        fontFamily: "Segoe UI Bold, sans-serif",
                        color: theme.palette.secondary.black,
                        textTransform: "none",
                        padding: "4px 8px",
                        borderRadius: "999px",
                        "&:hover": { backgroundColor: theme.palette.background.neutral },
                      }}
                      disableRipple
                    >
                    +{additionalApplicationsCount} {additionalApplicationsCount === 1 ? intl.formatMessage({ id: "recruiter.kanban.more_application" }) : intl.formatMessage({ id: "recruiter.kanban.more_applications" })}
                    </Button>
                    {null}
                  </>
                )}
              </>
            )}
          </Box>
        ) : null}

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}>
            {secondaryActionButtonLabel && typeof onSecondaryActionButtonClick === "function" ? (
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Button
                  disabled={secondaryActionDisabled}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSecondaryActionButtonClick();
                  }}
                  sx={{
                    textTransform: "none",
                    borderRadius: "999px",
                    padding: "6px 18px",
                    backgroundColor: theme.palette.text.main,
                    color: theme.palette.secondary.grey,
                    fontWeight: 800,
                    fontFamily: "Segoe UI Bold, sans-serif",
                    "&:hover": { backgroundColor: theme.palette.secondary.hover, color: theme.palette.text.main },
                    height: "auto",
                  }}
                >
                  {secondaryActionButtonLabel}
                </Button>
              </Box>
            ) : null}

            {typeof onActionButtonClick === "function" ? (
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onActionButtonClick();
                  }}
                  sx={{
                    textTransform: "none",
                    borderRadius: "999px",
                    padding: "6px 24px",
                    backgroundColor: theme.palette.common.white,
                    color: theme.palette.secondary.black,
                    fontWeight: 800,
                    fontFamily: "Segoe UI Bold, sans-serif",
                    border: `1px solid ${theme.palette.divider}`,
                    "&:hover": { backgroundColor: theme.palette.secondary.hover },
                    height: "auto",
                  }}
                >
                  {intl.formatMessage({ id: "recruiter.kanban.see_candidate" })}
                </Button>
              </Box>
            ) : null}

            {showMetaChips && candidate?.candidate_status && (
              <Button variant="text" className={classes.candidateStatusButton} disabled sx={{ backgroundColor: theme.palette.background.neutral }}>
                <FormattedMessage id={`candidate.status_map.${candidate?.candidate_status}`} />
              </Button>
            )}
            {experiencePastilleVisible ? (
              <Button variant="text" className={classes.candidateStatusButton} disabled sx={{ backgroundColor: "#FFF !important", border: `1px solid ${theme.palette.background.neutral} !important` }}>
                {(() => {
                  const expCount = parseInt(String(candidate.experience_year), 10);
                  if (Number.isFinite(expCount)) {
                    return (
                      <FormattedMessage
                        id="recruiter.candidate_experience_pastille"
                        defaultMessage="{count, plural, one {# year of experience} other {# years of experience}}"
                        values={{ count: expCount }}
                      />
                    );
                  }
                  return (
                    <>
                      {candidate.experience_year}{" "}
                      <FormattedMessage id="recruiter.candidate_experience_pastille" />
                    </>
                  );
                })()}
              </Button>
            ) : null}
            {showMetaChips && candidate?.region && (
              <Button variant="text" className={classes.candidateStatusButton} disabled sx={{ backgroundColor: "#FFF !important", border: `1px solid ${theme.palette.background.neutral} !important` }}>
                <Typography variant="body3">
                  {candidate?.region
                    ? intl.formatMessage({
                      id: `regions.${candidate.region}`,
                      defaultMessage: humanizeRegion(candidate.region),
                    })
                    : candidate?.city}
                </Typography>
              </Button>
            )}                             
          </Box>   
          {showSaveIcon ? (
            <Box
              sx={{
                display: pinSaveIconToBottom
                  ? "flex"
                  : isInformationPresent
                    ? "flex"
                    : isDesktop
                      ? "block"
                      : "none",
                alignItems: "center",
              }}
            >
              <Checkbox
                icon={<img src={SaveBorderIcon} alt="save" style={{ width: 24, height: 24 }} />}
                checkedIcon={<img src={SaveFilledIcon} alt="saved" style={{ width: 24, height: 24 }} />}
                checked={favorites.includes(candidate?.id) || false}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFavoriteToggle(candidate?.id, "toggle");
                }}
                sx={{ p: 0 }}
              />
            </Box>
          ) : null}
        </Box> 
      </Card>
      <CloseJobApplicationModal
        open={isCloseApplicationModalOpen}
        onClose={closeCloseApplicationModal}
        onConfirm={() => handleCloseJobApplication(pendingCloseApplication)}
        loading={Boolean(closingAppId)}
        candidateName={[candidate?.first_name, candidate?.last_name].filter(Boolean).join(" ")}
        jobTitle={
          pendingCloseApplication?.title ||
          pendingCloseApplication?.job_offer?.job_title ||
          intl.formatMessage({ id: "recruiter.kanban.spontaneous_application" })
        }
        denialReasons={selectedDenialReasons}
        otherReason={otherDenialReason}
        onToggleReason={toggleDenialReason}
        onChangeOtherReason={setOtherDenialReason}
      />
    </>
  );
};

export default CandidateCardNew;